/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */
import { ofType } from 'redux-observable';
import { of, empty, from, Observable } from 'rxjs';
import { ignoreElements, map, mergeMap } from 'rxjs/operators';
import type { Action } from '@reduxjs/toolkit';

import { ActionManagerInstance } from '../../../actions/manager';
import TargetMap from '../../../actions/targetmap';
import RemoteCase from '../../../case/remote-case';
import Constants, { publicConstants } from '../../../constants';
import { resolveRestApi } from '../../../router/app-router';
import { getResolvedTargetName } from '../../../router/routing-manager';
import { fetch } from '../../../service_broker/fetch';
import { getTranslatedDXAPIResponse } from '../../../utils/dxapi-response-translator';
import { logAndCatchError } from '../../../utils/rxjs-utils';
import Utils from '../../../utils/utils';
import * as actionType from '../actions';
import * as actions from '../actions/action-creators';
import {
  CONTAINER_NAMES,
  getActiveContainerItemName,
  getContainerItemName,
  getDataContextName,
  getTargetFromContainerItemID
} from '../../../container/container-utils';
import * as StateManager from '../state-manager';
import { publishCasePreviewEvent } from '../../../case/common-events';
import PubSubUtils from '../../../utils/pubsub-utils';
import HeaderProcessor from '../../../service_broker/header-processor';
import type { LoadViewPayload } from '../types';
import type { ActionWithPayload, Headers, RestAPIPayload, RootView } from '../../../types';
import ObjectTransformer from '../../../utils/object-transformer';
import { getCurrentCaseViewMode, isParentAndChildContextHoldingDifferentItem } from '../../../case/common-helper';
import CaseUtils from '../../../case/utils';

import {
  addPreviewResetAction,
  getActionBasedOnContainerMode,
  getActionPayload,
  getContext,
  getDirtyDetails,
  isAddContainerAction,
  processDataToDispatchErrorAction,
  processHeaders,
  processViewResponse,
  resolveShowPageActivation,
  setTraditionalRemoteCasePayloadParams,
  updateTraditionalGadgetId,
  updateTraditionalRemoteCaseAction
} from './util';

const { RESOURCE_TYPES } = publicConstants;

const addActionsForLoadView = (
  containerItemName: string,
  containerTarget: string | null,
  payload: { data: { caseInfo: { progress: boolean } }; root: any },
  dataToNextChain: any,
  containerName: string
) => {
  // If a preview container, then update with data, since the add container event would have already been dispatched in a loading state
  if (containerItemName && containerName === CONTAINER_NAMES.PREVIEW) {
    payload.data.caseInfo.progress = false;
    delete payload.root;
    dataToNextChain.actionsOut = actions.postProcess(actions.updateContainerItem, {
      ...payload,
      containerName
    });
  } else if (containerItemName) {
    dataToNextChain.actionsOut = [];
    dataToNextChain.actionsOut.push(
      actions.postProcess(actions.activateContainerItem, {
        target: containerTarget,
        containerItemID: containerItemName
      })
    );
  } else {
    dataToNextChain.actionsOut = actions.postProcess(actions.addContainerItem, {
      ...payload,
      containerName
    });
  }
};

const addMergeDataAction = (payload: LoadViewPayload, dataToNextChain: any) => {
  const isObjectCaseType = CaseUtils.isObjectCaseType(payload.caseClassName);
  // Push MERGE_DATA when there is a matching container item for the key, or when the
  // parent and child contexts are loaded and confirmed to be holding different items.
  // When updateData is true, always merge — the caller explicitly requested a data refresh.
  if (payload.key && !isObjectCaseType) {
    const containerTarget = getTargetFromContainerItemID(payload?.context);
    const containerItemName = getContainerItemName(containerTarget, payload.key) as string;
    const containerItemID = payload?.context;
    if (containerItemName || payload.updateData || isParentAndChildContextHoldingDifferentItem(containerItemID)) {
      dataToNextChain.actionsOut.push(actions.postProcess(actions.mergeData, payload));
    }
  } else {
    dataToNextChain.actionsOut.push(actions.postProcess(actions.mergeData, payload));
  }
};

const getTargetAndContext = (actionConfig: LoadViewPayload) => {
  const { containerName, updateData, viewLoadingContainer } = actionConfig;

  const vcTargetName = containerName || TargetMap.getTargetViewContainerName(actionType.ACTION_SHOWVIEW);
  const target = viewLoadingContainer?.target ?? getResolvedTargetName(vcTargetName, actionConfig.context);
  const context =
    viewLoadingContainer?.context ??
    (containerName === CONTAINER_NAMES.PREVIEW && updateData
      ? actionConfig.context
      : getContext(target, vcTargetName, actionConfig.context, {
          buildNew: !!containerName
        }));
  return { target, context };
};

const loadView = (action$: Observable<Action>) => {
  return action$.pipe(
    ofType(actionType.LOAD_VIEW),
    map((action: ActionWithPayload<LoadViewPayload>): LoadViewPayload => action.payload),
    mergeMap((payload: LoadViewPayload) => {
      const actionPayload = {
        caseID: payload.caseId,
        viewID: payload.viewName,
        caseClassName: payload.caseClassName,
        RemoteSystemID: payload.RemoteSystemID
      };
      // Adds preview container in a loading state while the request for data is being resolved
      if (
        payload.containerName === CONTAINER_NAMES.PREVIEW &&
        payload.context &&
        payload.caseId &&
        !payload.updateData
      ) {
        const target = getResolvedTargetName(CONTAINER_NAMES.PREVIEW, payload.context);
        const context = getContext(target, CONTAINER_NAMES.PREVIEW, payload.context, {
          buildNew: true
        });
        const key = decodeURI(payload.caseId);
        StateManager.dispatch(
          actions.addContainerItem({
            ...payload,
            context,
            target,
            containerItemID: context,
            key,
            caseViewMode: Constants.REVIEW_MODE,
            root: {
              type: 'reference',
              config: {
                context: Constants.getProperty(Constants.CASE_INFO.CASE_INFO_CONTENT),
                name: payload.viewName,
                type: 'view'
              }
            },
            data: {
              key,
              caseInfo: {
                ID: key,
                progress: true,
                error: false,
                caseTypeID: payload.caseClassName,
                content: {
                  classID: payload.caseClassName
                }
              }
            }
          })
        );
        payload.viewLoadingContainer = { target, context };
      }

      const headers: Headers = {};

      if (actionPayload.RemoteSystemID) {
        headers.RemoteSystemID = actionPayload.RemoteSystemID;
      } else if (RemoteCase.isRemoteCase(actionPayload.caseClassName)) {
        const { RemoteSystemID } = RemoteCase.getRemoteCase(actionPayload.caseClassName);
        headers.RemoteSystemID = RemoteSystemID;
      }
      let { restApi: restUrl, reqType }: RestAPIPayload = resolveRestApi('loadView', actionPayload) as RestAPIPayload;

      // If not in context of a work object then load like a page
      if (payload.viewContext === RESOURCE_TYPES.PAGE) {
        ({ restApi: restUrl, reqType } = resolveRestApi(actionType.ACTION_SHOWVIEW, {
          page: payload.viewName,
          pageClass: payload.pageClass
        }) as RestAPIPayload);
      }

      return from(
        fetch(
          restUrl,
          {
            method: reqType,
            headers
          },
          payload.context
        )
      ).pipe(
        map((response) => {
          const key = decodeURI(payload.caseId);
          let respJsonData: any = '';
          if (typeof response.data === 'string') {
            if (response.data) respJsonData = JSON.parse(response.data);
            else {
              console.error('response data is empty!!');
              respJsonData = {};
            }
          } else {
            respJsonData = response.data;
          }

          return {
            response: { ...respJsonData, key },
            actionConfig: payload,
            headers: processHeaders(response.headers),
            rootView: Utils.getIn(respJsonData, Constants.ROOT)
          };
        }),
        // This is the right way to detect a problem and call the promise's reject
        logAndCatchError(
          ': Error ocurred during ajax call at loadView Epic : ',
          payload.viewLoadingContainer?.context ?? payload.context
        )
      );
    }),
    mergeMap(
      async (data: {
        payload?: any;
        actionMgrID?: number;
        type: any;
        actionConfig: any;
        response: any;
        headers: any;
        rootView: any;
      }) => {
        const dataToNextChain: {
          actionsOut?: any;
          type?: string;
          actionMgrID?: number;
          rootView?: any;
        } = {};
        if (data?.type === actionType.ERROR) {
          // processDataToDispatchErrorAction calls ActionManagerInstance.rejectActionByID
          dataToNextChain.actionsOut = processDataToDispatchErrorAction(data);

          // Update preview container state to reflect error
          if (data?.payload?.context?.includes(CONTAINER_NAMES.PREVIEW)) {
            const previewContainerData = StateManager.getDataObject(data.payload.context);
            const updatedPreviewContainerData = {
              ...previewContainerData,
              caseInfo: { progress: false, error: true }
            };
            const target = getResolvedTargetName(CONTAINER_NAMES.PREVIEW, data.payload.context);
            dataToNextChain.actionsOut = actions.postProcess(actions.updateContainerItem, {
              key: updatedPreviewContainerData.key,
              target,
              containerName: CONTAINER_NAMES.PREVIEW,
              context: data.payload.context,
              containerItemID: data.payload.context,
              data: updatedPreviewContainerData
            });
          }
          return dataToNextChain;
        }
        try {
          dataToNextChain.actionMgrID = data?.actionConfig?.actionMgrID;
          dataToNextChain.rootView = data?.rootView;
          const { actionConfig, response, headers } = data;
          if (data?.actionConfig?.skipAssignmentsUpdate) {
            delete response?.data?.caseInfo?.assignments;
            delete response?.data?.caseInfo?.availableActions;
            delete response?.data?.caseInfo?.availableProcesses;
          }
          const { containerItemID, containerName } = actionConfig;
          const { target, context } = getTargetAndContext(actionConfig);

          actionConfig.context = context;
          const options = { acName: target, context };
          let translatedDXAPIResponse: { directUrlUpdation?: boolean; semanticURL?: any } = {};
          translatedDXAPIResponse = getTranslatedDXAPIResponse(response);
          translatedDXAPIResponse.directUrlUpdation = false;

          await processViewResponse(translatedDXAPIResponse, actionConfig, options);

          const commonInfo = {
            ...actionConfig,
            target,
            context,
            containerItemID: containerItemID || context,
            headers
          };
          const payload = getActionPayload(translatedDXAPIResponse, commonInfo);
          payload.resourceType = RESOURCE_TYPES.CASE;
          if (containerName && !payload.updateData) {
            /** Preview will be loaded in new context and no MERGE_DATA action is dispatched. */
            payload.caseViewMode = Constants.REVIEW_MODE;
            const containerTarget = getTargetFromContainerItemID(payload?.context);
            const containerItemName = getContainerItemName(containerTarget, payload.key) as string;
            addActionsForLoadView(containerItemName, containerTarget, payload, dataToNextChain, containerName);
          } else {
            // to retain client changes (entered or edited by user), need to set preserveClientChanges flag as true
            payload.preserveClientChanges = true;
            dataToNextChain.actionsOut = [];
            if (containerName === CONTAINER_NAMES.PREVIEW) {
              dataToNextChain.actionsOut.push({
                type: actionType.DELETE_CASE_SUMMARY,
                payload: {
                  context
                }
              });
            }
            addMergeDataAction(payload, dataToNextChain);
          }
        } catch (err) {
          console.error(err);
          dataToNextChain.rootView = {
            type: 'error',
            payload: err
          };
        }
        const { caseId, context: previewContext, viewName } = data.actionConfig;
        if (viewName === 'pyCasePreview') {
          publishCasePreviewEvent({ caseId, context: previewContext });
        }
        return dataToNextChain;
      }
    ),
    map(({ rootView, actionsOut, actionMgrID = 0 }) => {
      ActionManagerInstance.resolveActionByID(actionMgrID, rootView);
      ObjectTransformer.setNeedsMapping(false);
      return actionsOut;
    })
  );
};

const showPage = (action$: Observable<Action>) => {
  // Need to define these in this scope so they can be set/used
  //  in various parts of the processing.
  let actionMgrID = 0;
  let theReturn: any[] = [];

  return action$.pipe(
    ofType(actionType.SHOW_PAGE),
    map((action: any) => {
      // extract the Action Manager ID from the payload

      ({ actionMgrID } = action.payload);
      return action.payload;
    }),
    mergeMap((payload: any) => {
      PubSubUtils.publish(publicConstants.PUB_SUB_EVENTS.UI_EVENTS.SHOW_PAGE_STARTED);
      const {
        debugInfo,
        context,
        viewName: page,
        className: pageClass,
        skipSemanticUrl = false,
        containerName = TargetMap.getTargetViewContainerName(actionType.ACTION_SHOWVIEW)
      } = payload;

      const headers = debugInfo && { ...HeaderProcessor.addDebugHeaders(debugInfo) };
      const target = getResolvedTargetName(containerName, context);
      let semanticURL = '';
      let removeActionPayload = {};
      let continueExecution = true;

      if (!skipSemanticUrl) {
        const {
          semanticURL: resolvedSemanticURL,
          activate,
          activateContainerActionPayload
        } = resolveShowPageActivation(page, pageClass, target);
        semanticURL = resolvedSemanticURL;

        if (activate) {
          updateTraditionalGadgetId(activateContainerActionPayload);
          return of({
            type: actionType.ACTIVATE_CONTAINER_ITEM,
            activateContainerActionPayload,
            actionConfig: payload
          });
        }

        const activeContainerItemName = getActiveContainerItemName(target);

        if (activeContainerItemName) {
          ({ continueExecution, removeActionPayload } = getDirtyDetails(target));
        }

        if (!continueExecution) {
          ActionManagerInstance.rejectActionByID(actionMgrID);
          return empty();
        }
      }

      const { restApi: restUrl, reqType } = resolveRestApi(actionType.ACTION_SHOWVIEW, {
        page,
        pageClass
      });

      return from(
        fetch(
          restUrl,
          {
            method: reqType,
            headers
          },
          context
        )
      ).pipe(
        map((response) => {
          let respJsonData = {};
          if (typeof response.data === 'string') {
            respJsonData = JSON.parse(response.data);
          } else {
            respJsonData = response.data;
          }
          return {
            response: {
              ...respJsonData,
              key: semanticURL,
              semanticURL
            },
            actionConfig: payload,
            headers: processHeaders(response.headers),
            removeActionPayload,
            skipSemanticUrl
          };
        }),
        logAndCatchError(': Error ocurred during ajax call at showPage Epic : ', payload.context)
      );
    }),
    mergeMap(async (data) => {
      const dataToNextChain: {
        actionManagerID?: number;
        rootView?: RootView;
      } = {};
      dataToNextChain.actionManagerID = data?.actionConfig?.actionMgrID;
      if (data && data.type === actionType.ERROR) {
        // processDataToDispatchErrorAction calls ActionManagerInstance.rejectActionByID
        theReturn.push(processDataToDispatchErrorAction(data));
        return dataToNextChain;
      }

      let { type } = data;
      const { actionConfig, skipSemanticUrl } = data;

      const containerName = actionConfig.containerName || TargetMap.getTargetViewContainerName(actionType.SHOW_PAGE);
      const target = getResolvedTargetName(containerName, actionConfig.context);

      // Publish 'showPageInit' event before replacing existing container with new content.
      PubSubUtils.publish(publicConstants.PUB_SUB_EVENTS.UI_EVENTS.SHOW_PAGE_INIT, {
        pageName: data.actionConfig.viewName,
        pageClass: data.actionConfig.className
      });

      const { remoteCasePayload, updatedType } = updateTraditionalRemoteCaseAction(type, data, actionConfig, target);
      type = updatedType ?? type;

      if (actionConfig.isDeferLoaded) {
        const { response, headers = {} } = data;
        const { isCreateDataObject, context } = actionConfig;
        // Defer loaded tab pages should merge the data received
        const translatedDXAPIResponse = getTranslatedDXAPIResponse(response, {
          isCreateDataObject
        });
        translatedDXAPIResponse.directUrlUpdation = !skipSemanticUrl;
        const options = { acName: target, context };
        await processViewResponse(translatedDXAPIResponse, actionConfig, options);
        const commonInfo = {
          containerItemID: context,
          target,
          headers,
          context
        };
        const payload = getActionPayload(translatedDXAPIResponse, commonInfo);
        dataToNextChain.rootView = Utils.getIn(translatedDXAPIResponse, Constants.ROOT);
        theReturn.push(actions.postProcess(actions.mergeData, payload));
      } else if (type === actionType.ACTIVATE_CONTAINER_ITEM) {
        const {
          activateContainerActionPayload: { semanticURL, containerItemID }
        } = data;
        const context = getDataContextName(containerItemID);
        const options = { acName: target, context };
        // ignore view response while launching traditional remote case
        if (!actionConfig.isTraditionalRemoteCase) {
          await processViewResponse({ directUrlUpdation: true, semanticURL }, actionConfig, options, true);
        }
        theReturn.push(actions.postProcess(actions.activateContainerItem, { target, containerItemID }));
      } else {
        const context = getContext(target, containerName, actionConfig.context);
        const { response, headers = {}, removeActionPayload } = data;
        const translatedDXAPIResponse = getTranslatedDXAPIResponse(response, {
          isCreateDataObject: actionConfig.isCreateDataObject
        });
        translatedDXAPIResponse.directUrlUpdation = !skipSemanticUrl;
        const options = { acName: target, context };

        const currentCaseViewMode = getCurrentCaseViewMode(context as string);
        await processViewResponse(translatedDXAPIResponse, actionConfig, options);
        const commonInfo = {
          target,
          context,
          containerItemID: context,
          headers,
          debugInfo: actionConfig.debugInfo,
          currentCaseViewMode,
          referenceList: actionConfig?.referenceListStr,
          subscriptName: actionConfig?.subscriptName
        };
        const payload = getActionPayload(translatedDXAPIResponse, commonInfo);
        payload.resourceType = publicConstants.PAGE;
        if (actionConfig.isCreateDataObject) {
          payload.resourceType = publicConstants.RESOURCE_TYPES.DATA;
          payload.resourceStatus = publicConstants.RESOURCE_STATUS.CREATE;
          delete payload.data.caseInfo;
          payload.referenceList = actionConfig.referenceListStr;
          payload.subscriptName = actionConfig.subscriptName;
        }
        setTraditionalRemoteCasePayloadParams(actionConfig, remoteCasePayload, payload);
        payload.data ??= {};
        payload.data.traditionalGadgetId = CaseUtils.getUniqueGadgetId();
        const action = getActionBasedOnContainerMode(target, payload);
        if (removeActionPayload.containerItemID && isAddContainerAction(action)) {
          theReturn.push(
            actions.postProcess(actions.removeContainerItem, {
              containerItemID: removeActionPayload.containerItemID,
              target
            })
          );
        }
        theReturn.push(action);
        addPreviewResetAction(theReturn);
      }

      // Publish event for when a page is displayed
      PubSubUtils.publish(publicConstants.PUB_SUB_EVENTS.UI_EVENTS.SHOW_PAGE_DONE, {
        target,
        pageName: data.actionConfig.viewName,
        pageClass: data.actionConfig.className
      });

      return dataToNextChain;
    }),
    map(({ actionManagerID = 0, rootView }) => {
      // this is the right place to note that the promise should resolve...
      ActionManagerInstance.resolveActionByID(actionManagerID, rootView);
      StateManager.dispatch(theReturn);
      theReturn = [];
    }),
    ignoreElements()
  );
};

const uiEpics = [loadView, showPage];

export default uiEpics;
